import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSim } from "@/context/SimContext";
import { Card, SectionTitle, LearnLink } from "@/components/ui-kit";
import { UserCheck, ArrowRight } from "lucide-react";

const DECISION_TONE = {
  APPROVE: "text-emerald-400 border-emerald-800 bg-emerald-950/30",
  CORRECT: "text-blue-400 border-blue-800 bg-blue-950/30",
  REJECT: "text-red-400 border-red-800 bg-red-950/30",
  STOP: "text-red-300 border-red-700 bg-red-950/50",
};

export default function HumanDecisions() {
  const { eventTick } = useSim();
  const [items, setItems] = useState([]);

  useEffect(() => {
    const load = async () => {
      try { const { data } = await api.get("/interventions"); setItems(data); } catch {}
    };
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [eventTick]);

  return (
    <div className="space-y-6 ngx-fade-in">
      <div className="flex items-center justify-between">
        <SectionTitle sub="Every intervention is recorded as structured, auditable data.">Human Decisions</SectionTitle>
        <LearnLink to="/academy/lesson/hitl" label="Learn: Human-in-the-Loop" testid="learn-hitl" />
      </div>

      <Card className="p-4 border-blue-900/50 bg-blue-950/10">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 font-display text-sm font-medium text-slate-300">
          {["Machine Proposal", "Human Decision", "Action", "Result", "Knowledge"].map((s, i, arr) => (
            <span key={s} className="flex items-center gap-3">
              <span className="px-3 py-1 rounded-sm bg-[#050B14] border border-slate-700 text-slate-100">{s}</span>
              {i < arr.length - 1 && <ArrowRight className="h-4 w-4 text-slate-600" />}
            </span>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Human feedback data available for future model improvement and learning workflows (dataset generation,
          model evaluation, learning by demonstration) — labelled as future capabilities.
        </p>
      </Card>

      {items.length === 0 && (
        <Card className="p-10 text-center text-slate-500" data-testid="no-decisions">
          <UserCheck className="h-8 w-8 mx-auto mb-3 text-slate-600" />
          No human interventions recorded yet. Trigger a scenario from the Demo panel.
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="decision-history">
        {items.map((it) => (
          <Card key={it.id} className="p-5 ngx-fade-in">
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono text-sm text-slate-100">{it.event_no}</span>
              <span className={`px-2.5 py-0.5 rounded-sm text-[11px] font-semibold uppercase tracking-wider border ${DECISION_TONE[it.decision] || DECISION_TONE.APPROVE}`}>
                {it.decision}
              </span>
            </div>
            <div className="space-y-2 text-sm font-mono">
              <Row label="System" value={it.system} />
              <Row label="AI Confidence" value={`${it.ai_confidence}%`} valueClass={it.ai_confidence < 75 ? "text-amber-400" : "text-emerald-400"} />
              <Row label="AI Proposal" value={it.ai_proposal} />
              {it.human_correction && <Row label="Human Correction" value={it.human_correction} valueClass="text-blue-300" />}
              {it.destination && <Row label="Destination" value={it.destination} />}
              <Row label="Result" value={it.result} valueClass="text-emerald-400" />
              <Row label="Operator" value={it.operator} />
              <Row label="Timestamp" value={it.time} valueClass="text-slate-400" />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Row({ label, value, valueClass = "text-slate-100" }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
      <span className={valueClass}>{value}</span>
    </div>
  );
}
